import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import type { AuthState, MfaEnrollResponse } from '@jarvis/shared';
import { APP_VERSION } from '@jarvis/shared/constants';
import { Ambient } from '../components/Ambient';
import { Hologram } from '../components/hud/Hologram';
import { HudStage } from '../components/hud/Stage';
import { Clock, Led, Panel } from '../components/hud/widgets';
import { connections, useApiStatus } from '../lib/health';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { authStore } from '../lib/auth';

const msg = (e: unknown) => (e instanceof Error ? e.message : 'Algo deu errado.');

const STEPS = ['E-mail', 'Link', 'Segundo fator'];

/* Circuitos ligando os painéis ao holograma. */
function loginTraces(W: number, H: number): [number, number][][] {
  const c = W / 2, m = H / 2;
  return [
    [[c + 40, m - 110], [c - 20, m - 110], [c - 160, m - 64]],
    [[c + 40, m + 90], [c - 10, m + 90], [c - 50, m + 110]],
    [[200, 112], [200, 170], [250, 215]],
    [[W - 160, 104], [W - 160, 150], [W - 200, 190]]
  ];
}

function ServerPanel() {
  const api = useApiStatus();
  const conn = connections(api);
  return (
    <Panel title="Servidor" className="lg-server" tag={api.state === 'up' ? 'no ar' : api.state === 'checking' ? '…' : 'desligado'}>
      <div className="lg-leds">
        <Led on={api.state === 'up'} label="API" />
        <Led on={conn.banco} label="Banco" />
        <Led on={conn.login} label="Login" />
      </div>
    </Panel>
  );
}

function AuthFrame({ title, lead, children, step, offline = false }: {
  title: string; lead: ReactNode; children: ReactNode; step: number; offline?: boolean;
}) {
  return (
    <>
      <Ambient hud />
      <HudStage label="Acesso ao Jarvis" className="hx-login" traces={loginTraces}>
        <header className="hx-w hx-title">
          <h1>JARVIS <span>MASTER</span></h1>
          <p>ACESSO SEGURO · LINK POR E-MAIL + SEGUNDO FATOR</p>
        </header>
        <Clock />
        <ServerPanel />

        <div className="lg-holo">
          <Hologram variant="login" signal={{ level: 0, listening: false, speaking: false, alert: false, online: !offline }} />
          <div className="lg-readout">
            <span className="hr-state">{offline ? 'SERVIDOR INDISPONÍVEL' : step === 2 ? 'VERIFICAÇÃO EM DUAS ETAPAS' : 'IDENTIFICAÇÃO NECESSÁRIA'}</span>
            <span className="hr-sub">só e-mails autorizados neste Jarvis entram</span>
          </div>
        </div>

        <section className="hx-w lg-card" aria-labelledby="lg-title">
          <div className="lg-scan" aria-hidden="true" />
          <div className="lg-brand"><Icon name="logo" />JARVIS MASTER</div>
          <ol className="lg-steps" aria-label="Etapas do acesso">
            {STEPS.map((s, i) => (
              <li key={s} className={i < step ? 'done' : i === step ? 'now' : ''} aria-current={i === step ? 'step' : undefined}>{s}</li>
            ))}
          </ol>
          <h1 id="lg-title">{title}</h1>
          <p className="auth-lead">{lead}</p>
          {children}
          <p className="lg-meta"><span>v{APP_VERSION}</span><span>compilado em {__BUILD__} UTC</span></p>
        </section>

        <aside className="hx-w lg-side" aria-label="Como o acesso é protegido">
          <Panel title="Protocolo">
            <dl>
              <div><dt>Acesso</dt><dd>Link de uso único, sem senha</dd></div>
              <div><dt>Segundo fator</dt><dd>Código de 6 números (TOTP)</dd></div>
            </dl>
          </Panel>
          <Panel title="Sessão">
            <dl>
              <div><dt>Guardada em</dt><dd>Cookie protegido (HttpOnly)</dd></div>
              <div><dt>Quem entra</dt><dd>Só e-mails autorizados</dd></div>
            </dl>
          </Panel>
        </aside>
      </HudStage>
    </>
  );
}
function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    setSlow(false);
    /* Depois de alguns segundos, explica a demora em vez de deixar a tela muda. */
    const t = window.setTimeout(() => setSlow(true), 6000);
    try { await fn(); } catch (e) {
      setError(e instanceof ApiError && e.fields ? Object.values(e.fields)[0] ?? e.message : msg(e));
    } finally {
      window.clearTimeout(t);
      setBusy(false);
      setSlow(false);
    }
  };
  return { busy, slow, error, setError, run };
}

/* Erro vindo do retorno do link (?login=falhou): mostra uma vez e limpa o endereço. */
function takeLinkError(): string {
  const params = new URLSearchParams(window.location.search);
  if (params.get('login') !== 'falhou') return '';
  history.replaceState(null, '', window.location.pathname + window.location.hash);
  return 'O link não funcionou. Ele vale uma vez só, expira, e precisa ser aberto neste mesmo navegador. Peça um novo.';
}

type Status = { kind: 'wait' | 'ok' | 'err'; text: string; code?: string };

export function EmailLogin() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [linkError] = useState(takeLinkError);
  const [status, setStatus] = useState<Status | null>(null);
  const [slow, setSlow] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const busy = status?.kind === 'wait';

  /* Se o link for aberto em outra aba, esta percebe a sessão nova ao voltar a ficar visível. */
  useEffect(() => {
    if (!sent) return;
    const check = () => { if (document.visibilityState === 'visible') authStore.refresh(); };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => { document.removeEventListener('visibilitychange', check); window.removeEventListener('focus', check); };
  }, [sent]);

  /* Esta tela nunca pode ficar muda. Toda tentativa termina numa linha de estado,
     com sucesso ou com o código do erro, para o problema nunca ser invisível.
     O valor é lido direto do campo: se o navegador preencher sozinho, o estado do
     React pode não acompanhar. */
  const requestLink = async (e?: Pick<FormEvent, 'preventDefault'>) => {
    e?.preventDefault();
    if (busy) return;
    const value = (field.current?.value ?? email).trim();
    if (!value.includes('@') || value.length < 5) {
      setStatus({ kind: 'err', text: 'Informe um e-mail válido.', code: 'CAMPO' });
      return;
    }
    setEmail(value);
    setStatus({ kind: 'wait', text: 'Falando com o servidor…' });
    setSlow(false);
    const t = window.setTimeout(() => setSlow(true), 6000);
    try {
      await api.post('/api/auth/email-link', { email: value });
      setStatus({ kind: 'ok', text: 'Pedido aceito pelo servidor.' });
      setSent(true);
    } catch (err) {
      setStatus({ kind: 'err', text: msg(err), code: err instanceof ApiError ? err.code : 'FALHA' });
    } finally {
      window.clearTimeout(t);
      setSlow(false);
    }
  };

  if (!sent) {
    return (
      <AuthFrame step={0} title="Entrar" lead="Enviaremos um link de acesso para o seu e-mail. Só e-mails autorizados neste Jarvis conseguem entrar.">
        <form className="stack" onSubmit={requestLink} noValidate>
          {linkError && <p className="field-error" role="alert">{linkError}</p>}
          <div className="field"><label htmlFor="login-email">E-mail</label>
            <input id="login-email" ref={field} className="input" type="email" autoComplete="email" autoFocus required
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(ev) => { if (ev.key === 'Enter') requestLink(ev); }} /></div>
          {/* type="button": o clique não depende do envio de formulário, que a política
              de segurança do site bloqueia. */}
          <button className="btn btn-primary" type="button" disabled={busy} onClick={() => requestLink()}>
            {busy ? 'Enviando…' : 'Enviar link de acesso'}
          </button>
          {status && (
            <p className={status.kind === 'err' ? 'field-error' : 'auth-status'} role="status">
              {status.kind === 'wait' ? '· ' : status.kind === 'ok' ? '✓ ' : ''}{status.text}
              {status.code && <span className="mono"> [{status.code}]</span>}
            </p>
          )}
          {slow && (
            <div className="notice"><Icon name="info" /><span>Ainda enviando. O servidor gratuito hiberna quando fica sem uso e pode levar até um minuto para acordar.</span></div>
          )}
        </form>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame step={1} title="Confira seu e-mail" lead={<>Se <strong>{email}</strong> estiver autorizado, chegou um e-mail com o assunto <strong>“Your sign-in link”</strong>. Clique em <strong>Sign in</strong>.</>}>
      <div className="stack">
        <div className="notice"><Icon name="info" /><span>Abra o link <strong>neste mesmo navegador</strong>. Por segurança, ele não funciona em outro aparelho ou navegador, vale uma vez só e expira.</span></div>
        <button className="btn btn-primary" type="button" onClick={() => authStore.refresh()}>Já cliquei no link</button>
        <button className="btn btn-ghost" type="button" onClick={() => setSent(false)}>Usar outro e-mail ou pedir novo link</button>
      </div>
    </AuthFrame>
  );
}

function CodeForm({ factorId, submitLabel }: { factorId?: string; submitLabel: string }) {
  const [code, setCode] = useState('');
  const { busy, error, run } = useSubmit();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    run(async () => authStore.set(await api.post<AuthState>('/api/auth/mfa/verify', { code, factorId })));
  };
  return (
    <form className="stack" onSubmit={submit} noValidate>
      <div className="field"><label htmlFor="mfa-code">Código de 6 números do aplicativo autenticador</label>
        <input id="mfa-code" className="input input-code" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6}
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></div>
      {error && <p className="field-error" role="alert">{error}</p>}
      <button className="btn btn-primary" type="submit" disabled={busy || code.length !== 6}>{busy ? 'Conferindo…' : submitLabel}</button>
    </form>
  );
}

async function logout() {
  authStore.set(await api.post<AuthState>('/api/auth/logout'));
}

export function MfaEnroll() {
  const [data, setData] = useState<MfaEnrollResponse | null>(null);
  const [error, setError] = useState('');
  const requested = useRef(false);
  useEffect(() => {
    // Um pedido só: cada cadastro novo invalida o QR code anterior.
    if (requested.current) return;
    requested.current = true;
    api.post<MfaEnrollResponse>('/api/auth/mfa/enroll').then(setData, (e) => setError(msg(e)));
  }, []);
  return (
    <AuthFrame step={2} title="Proteja sua conta" lead="O Jarvis exige um segundo fator. Use um aplicativo autenticador (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…) para ler o QR code abaixo.">
      {error && <p className="field-error" role="alert">{error}</p>}
      {!data && !error && <p className="row-sub">Gerando QR code…</p>}
      {data && (
        <div className="stack">
          <img className="mfa-qr" src={data.qrCode} alt="QR code para cadastrar o Jarvis no aplicativo autenticador" width={200} height={200} />
          <details className="more">
            <summary><Icon name="info" />Não consegue ler o QR code?</summary>
            <p className="row-sub">Digite esta chave no aplicativo: <span className="mono secret">{data.secret}</span></p>
          </details>
          <CodeForm factorId={data.factorId} submitLabel="Ativar e entrar" />
        </div>
      )}
      <button className="btn btn-ghost" type="button" onClick={logout}>Sair</button>
    </AuthFrame>
  );
}

export function MfaVerify() {
  return (
    <AuthFrame step={2} title="Segundo fator" lead="Abra o aplicativo autenticador e digite o código atual do Jarvis Master.">
      <CodeForm submitLabel="Entrar" />
      <button className="btn btn-ghost" type="button" onClick={logout}>Sair</button>
    </AuthFrame>
  );
}

export function ServerDown({ message }: { message: string }) {
  return (
    <AuthFrame step={0} offline title="Servidor indisponível" lead={message}>
      <button className="btn btn-primary" type="button" onClick={() => authStore.refresh()}><Icon name="refresh" />Tentar novamente</button>
    </AuthFrame>
  );
}
